import {
  collection,
  limit as fsLimit,
  onSnapshot,
  orderBy,
  query,
  Unsubscribe
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { apiClient } from './apiClient';
import { Message, SendMessageInput } from '../../types/models/whatsapp';

const CONVERSATIONS = 'whatsapp_conversations';
const MESSAGE_PAGE_SIZE = 100;

/** One row in the sent-message log. Flatter than Message — this is a table. */
export interface SentMessage {
  id: string;
  conversationId: string | null;
  contactId: string | null;
  contactName: string;
  contactPhone: string;
  wamid: string | null;
  type: string;
  status: string;
  text: string;
  templateName: string | null;
  broadcastId: string | null;
  broadcastName: string | null;
  sentByAgentName: string | null;
  automationRunId: string | null;
  error: { code: number; title: string; details: string } | null;
  createdAt: number;
  statusTimestamps: Record<string, number>;
}


class MessageService {
  private mapMessage(id: string, data: any): Message {
    return {
      id,
      wamid: data.wamid ?? null,
      conversationId: data.conversationId,
      contactId: data.contactId,
      phoneNumberId: data.phoneNumberId,
      direction: data.direction,
      type: data.type,
      status: data.status || 'queued',
      text: data.text,
      media: data.media,
      template: data.template,
      interactive: data.interactive,
      location: data.location,
      reaction: data.reaction,
      system: data.system,
      replyToWamid: data.replyToWamid,
      error: data.error,
      sentByAgentId: data.sentByAgentId,
      sentByAgentName: data.sentByAgentName,
      automationRunId: data.automationRunId,
      broadcastId: data.broadcastId,
      broadcastName: data.broadcastName,
      createdAt: data.createdAt || 0,
      statusTimestamps: data.statusTimestamps || {}
    };
  }

  /**
   * Live thread, oldest-first for rendering. Queried newest-first so the limit
   * takes the most recent page, then reversed.
   */
  subscribeToMessages(
    conversationId: string,
    onChange: (messages: Message[]) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    if (!db) {
      onError?.(new Error('Firestore unavailable'));
      return () => {};
    }

    const q = query(
      collection(db, CONVERSATIONS, conversationId, 'messages'),
      orderBy('createdAt', 'desc'),
      fsLimit(MESSAGE_PAGE_SIZE)
    );

    return onSnapshot(
      q,
      (snapshot) => {
        const messages = snapshot.docs
          .map((d) => this.mapMessage(d.id, d.data()))
          .reverse();
        onChange(messages);
      },
      (error) => {
        console.error('❌ Message subscription error:', error);
        onError?.(error as Error);
      }
    );
  }

  /**
   * Sends via the API, which enforces the 24h window and holds the access
   * token. The server writes an optimistic `queued` message immediately, so the
   * live subscription renders it before Meta responds.
   */
  async send(input: SendMessageInput): Promise<{ messageId: string }> {
    return apiClient.post<{ messageId: string }>('/messages', input);
  }

  /**
   * Retries a failed send.
   *
   * conversationId is required: messages live in a subcollection, and a
   * collection-group lookup by bare document id is rejected by Firestore
   * ("not a valid document path"). Passing it makes this a direct read.
   */
  async retry(messageId: string, conversationId: string): Promise<void> {
    await apiClient.post(`/messages/${messageId}/retry`, { conversationId });
  }

  /** Older pages, served from the Postgres archive rather than Firestore. */
  async loadHistory(
    conversationId: string,
    before: number,
    limit = MESSAGE_PAGE_SIZE
  ): Promise<Message[]> {
    const response = await apiClient.get<{ messages: Message[] }>(
      `/conversations/${conversationId}/messages?before=${before}&limit=${limit}`
    );
    return response.messages;
  }

  /**
   * Every outbound message across all conversations, newest first.
   *
   * Separate from loadHistory, which is scoped to one thread. This is the
   * audit view: what went out, to whom, and whether it landed.
   */
  async listSent(params: {
    status?: string;
    type?: string;
    search?: string;
    before?: number;
    limit?: number;
  } = {}): Promise<{ messages: SentMessage[]; nextCursor: number | null }> {
    const query = new URLSearchParams();
    if (params.status) query.set('status', params.status);
    if (params.type) query.set('type', params.type);
    if (params.search) query.set('search', params.search);
    if (params.before) query.set('before', String(params.before));
    query.set('limit', String(params.limit || 50));

    return apiClient.get<{ messages: SentMessage[]; nextCursor: number | null }>(
      `/messages/sent?${query.toString()}`
    );
  }
}

export const messageService = new MessageService();
export default messageService;
